import { marked } from 'marked';
import TurndownService from 'turndown';

export type ContentFormat = 'markdown' | 'html' | 'json';

export interface ConversionOptions {
  sourceFormat: ContentFormat;
  targetFormat: ContentFormat;
  content: string;
  metadata?: {
    title?: string;
    tags?: string[];
    excerpt?: string;
  };
}

export class ContentConverter {
  /**
   * Convert content from one format to another
   */
  static async convert(options: ConversionOptions): Promise<string> {
    const { sourceFormat, targetFormat, content, metadata } = options;

    // If formats are the same, return as-is
    if (sourceFormat === targetFormat) {
      return content;
    }

    // Convert from markdown
    if (sourceFormat === 'markdown') {
      if (targetFormat === 'html') {
        return await this.markdownToHtml(content);
      } else if (targetFormat === 'json') {
        return await this.markdownToJson(content, metadata);
      }
    }

    // Convert from html
    if (sourceFormat === 'html') {
      if (targetFormat === 'markdown') {
        // Basic HTML to markdown conversion (simplified)
        return this.htmlToMarkdown(content);
      } else if (targetFormat === 'json') {
        return this.htmlToJson(content, metadata);
      }
    }

    // Convert from json
    if (sourceFormat === 'json') {
      try {
        const parsed = JSON.parse(content);
        if (targetFormat === 'html') {
          return parsed.content || content;
        } else if (targetFormat === 'markdown') {
          return parsed.content || content;
        }
      } catch (e) {
        // If JSON parsing fails, return original content
        return content;
      }
    }

    // Fallback: return original content
    return content;
  }

  /**
   * Convert markdown to HTML using marked library
   */
  private static async markdownToHtml(markdown: string): Promise<string> {
    // Configure marked for better HTML output
    marked.setOptions({
      gfm: true, // GitHub Flavored Markdown
      breaks: true, // Convert line breaks to <br>
    });

    return await marked(markdown);
  }

  /**
   * Convert markdown to JSON structure
   */
  private static async markdownToJson(markdown: string, metadata?: any): Promise<string> {
    const html = await this.markdownToHtml(markdown);
    
    const jsonStructure = {
      content: html,
      contentFormat: 'html',
      originalFormat: 'markdown',
      ...metadata
    };

    return JSON.stringify(jsonStructure, null, 2);
  }

  /**
   * Convert HTML to markdown using turndown library
   */
  private static htmlToMarkdown(html: string): string {
    const turndownService = new TurndownService({
      headingStyle: 'atx',
      bulletListMarker: '-',
      codeBlockStyle: 'fenced',
      fence: '```',
      emDelimiter: '*',
      strongDelimiter: '**',
      linkStyle: 'inlined',
      linkReferenceStyle: 'full'
    });

    return turndownService.turndown(html);
  }

  /**
   * Convert HTML to JSON structure
   */
  private static htmlToJson(html: string, metadata?: any): string {
    const jsonStructure = {
      content: html,
      contentFormat: 'html',
      originalFormat: 'html',
      ...metadata
    };

    return JSON.stringify(jsonStructure, null, 2);
  }

  /**
   * Prepare content for publishing to a specific platform
   */
  static async prepareForPublishing(
    content: string,
    targetFormat: ContentFormat,
    metadata?: {
      title?: string;
      tags?: string[];
      excerpt?: string;
    }
  ): Promise<string> {
    const sanitizedContent = this.sanitizeMarkdownForPublishing(content);

    return await this.convert({
      sourceFormat: 'markdown', // Assume source is always markdown from our editor
      targetFormat,
      content: sanitizedContent,
      metadata
    });
  }

  /**
   * Sanitize markdown to remove custom syntax before publishing
   */
  private static sanitizeMarkdownForPublishing(markdown: string): string {
    // Remove the custom image size syntax ` =600x400` to make it a standard markdown image.
    const customImageRegex = /\!\[(.*?)\]\((.*?)\s*=\s*.*\)/g;
    return markdown.replace(customImageRegex, '![$1]($2)');
  }
}
